/**
 * Game Start Event Collector for Poker Reborn
 *
 * Collects events from GameEventBus (hand:start, blinds:posted, cards:dealt:hero)
 * and triggers GameStartSequencer when all required events have arrived.
 *
 * Trust model: poker_client trusts poker_platform 100%.
 * No timeout or fallback logic - if messages don't arrive, that's a server issue.
 *
 * @see docs/systems/animations/GAME_START_SEQUENCER_PLAN.md
 * @see docs/systems/animations/GAME_START_ANIMATION.md
 */

import { gameEventBus } from '@/effects/events/GameEventBus';
import { tableState } from '@/state/TableState';
import { log } from '@/utils/log';
import { gameStartSequencer } from './GameStartSequencer';
import type {
  HandStartEvent,
  BlindsPostedEvent,
  HoleCardsDealtEvent,
} from '@/effects/events/GameEvents';
import type { Card } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

export interface BlindsData {
  smallBlind: {
    seat: number;
    amount: number;
    playerId: string;
  };
  bigBlind: {
    seat: number;
    amount: number;
    playerId: string;
  };
}

// =============================================================================
// GAME START EVENT COLLECTOR CLASS
// =============================================================================

/**
 * Collects game-start events and triggers the sequencer when complete.
 *
 * Collection requirements differ based on hero's role:
 * - Player (heroSeat !== null): Needs start + blinds + cards
 * - Spectator (heroSeat === null): Needs start + blinds only
 */
export class GameStartEventCollector {
  private static _instance: GameStartEventCollector | null = null;

  /** Pending hand start data */
  private pendingStart: HandStartEvent | null = null;

  /** Pending blinds data */
  private pendingBlinds: BlindsData | null = null;

  /** Pending hero cards */
  private pendingHeroCards: Card[] | null = null;

  /** Current game number being collected */
  private currentGameNumber: number | null = null;

  /** Whether collection is active */
  private _isCollecting = false;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {
    this.subscribeToEvents();
  }

  /**
   * Get the singleton instance.
   */
  static getInstance(): GameStartEventCollector {
    if (!GameStartEventCollector._instance) {
      GameStartEventCollector._instance = new GameStartEventCollector();
    }
    return GameStartEventCollector._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    GameStartEventCollector._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check if collection is in progress.
   */
  get isCollecting(): boolean {
    return this._isCollecting;
  }

  // ---------------------------------------------------------------------------
  // Event Subscriptions
  // ---------------------------------------------------------------------------

  private subscribeToEvents(): void {
    gameEventBus.on('hand:start', (data) => { this.onHandStart(data); });
    gameEventBus.on('blinds:posted', (data) => { this.onBlindsPosted(data); });
    gameEventBus.on('cards:dealt:hero', (data) => { this.onHeroCardsDealt(data); });

    log.effects.info`GameStartEventCollector: subscribed to events`;
  }

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  private onHandStart(data: HandStartEvent): void {
    // If new hand starts while collecting previous, reset
    if (this._isCollecting && this.currentGameNumber !== data.gameNumber) {
      log.effects.warning`GameStartEventCollector: new hand ${data.gameNumber} started, aborting collection for hand ${this.currentGameNumber}`;
      this.reset();
    }

    this._isCollecting = true;
    this.currentGameNumber = data.gameNumber;
    this.pendingStart = data;

    log.effects.debug`GameStartEventCollector: hand:start received (game ${data.gameNumber}, dealer seat ${data.dealerSeat})`;

    this.checkComplete();
  }

  private onBlindsPosted(data: BlindsPostedEvent): void {
    if (!this._isCollecting) {
      log.effects.warning`GameStartEventCollector: blinds:posted received but not collecting`;
      return;
    }

    this.pendingBlinds = {
      smallBlind: {
        seat: data.smallBlind.seat,
        amount: data.smallBlind.amount,
        playerId: data.smallBlind.playerId,
      },
      bigBlind: {
        seat: data.bigBlind.seat,
        amount: data.bigBlind.amount,
        playerId: data.bigBlind.playerId,
      },
    };

    log.effects.debug`GameStartEventCollector: blinds:posted received (SB seat ${data.smallBlind.seat}, BB seat ${data.bigBlind.seat})`;

    this.checkComplete();
  }

  private onHeroCardsDealt(data: HoleCardsDealtEvent): void {
    if (!this._isCollecting) {
      log.effects.warning`GameStartEventCollector: cards:dealt:hero received but not collecting`;
      return;
    }

    this.pendingHeroCards = data.cards;

    log.effects.debug`GameStartEventCollector: cards:dealt:hero received`;

    this.checkComplete();
  }

  // ---------------------------------------------------------------------------
  // Completion Check
  // ---------------------------------------------------------------------------

  private checkComplete(): void {
    const heroSeat = tableState.heroSeat.get();
    const isSpectator = heroSeat === null;

    // Different completion requirements based on role
    const hasStart = this.pendingStart !== null;
    const hasBlinds = this.pendingBlinds !== null;
    const hasCards = this.pendingHeroCards !== null;

    // Spectators don't need cards - complete with just start + blinds
    const isComplete = isSpectator
      ? hasStart && hasBlinds
      : hasStart && hasBlinds && hasCards;

    if (!isComplete) {
      log.effects.debug`GameStartEventCollector: not complete yet (start: ${hasStart}, blinds: ${hasBlinds}, cards: ${hasCards}, spectator: ${isSpectator})`;
      return;
    }

    // All data collected - trigger sequencer
    log.effects.info`GameStartEventCollector: collection complete, triggering sequencer`;

    void gameStartSequencer.playSequence({
      gameNumber: this.pendingStart!.gameNumber,
      dealerSeat: this.pendingStart!.dealerSeat,
      blinds: this.pendingBlinds!,
      heroCards: this.pendingHeroCards,
      isSpectator,
    });

    // Reset for next hand
    this.reset();
  }

  // ---------------------------------------------------------------------------
  // Reset
  // ---------------------------------------------------------------------------

  private reset(): void {
    this.pendingStart = null;
    this.pendingBlinds = null;
    this.pendingHeroCards = null;
    this.currentGameNumber = null;
    this._isCollecting = false;
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global game start event collector instance.
 */
export const gameStartEventCollector = GameStartEventCollector.getInstance();
